import { focusManager, type Query, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { ApiError } from "./api";
import { connectivity } from "./connectivity";
import { clearAllPrivateCaches } from "./privateCache";
import { writeActivity, type WriteActivity } from "./writeActivity";

export const HOUSEHOLD_REFRESH_MS = 10_000;
const READS = new Set(["recipes", "recipe", "categories", "grocery-lists", "grocery-list", "pantry", "plan", "menus", "menu", "cook-sessions", "timers", "detail", "invitations", "api-keys"]);
/** Explicit allowlist: no AI previews, exports, photos or one-shot/private generated results. */
export function isHouseholdRead(key: QueryKey) {
  return key[0] === "me" || key[0] === "h" && typeof key[1] === "string" && READS.has(String(key[2]));
}
export function isDraftDetail(key: QueryKey, pathname: string) {
  if (key[0] !== "h") return false;
  return key[2] === "recipe" && /^\/recipes\/[^/]+\/edit\/?$/.test(pathname)
    || key[2] === "menu" && /^\/plan\/menus\/[^/]+\/?$/.test(pathname);
}
interface SyncEnvironment {
  visible: () => boolean;
  online: () => boolean;
  pathname: () => string;
  activity: WriteActivity;
  subscribeWake: (listener: () => void) => () => void;
}
const browserEnvironment = (): SyncEnvironment => ({
  visible: () => typeof document === "undefined" || document.visibilityState === "visible",
  online: () => connectivity.isWritable(),
  pathname: () => typeof location === "undefined" ? "" : location.pathname,
  activity: writeActivity,
  subscribeWake: (listener) => {
    const focus = focusManager.subscribe((focused) => { if (focused) listener(); });
    const reconnect = connectivity.onReconnect(listener);
    return () => { focus(); reconnect(); };
  },
});

/** One scheduler per app, only observed/enabled GET queries, with no overlapping rounds. */
export function installHouseholdSync(client: QueryClient, environment: SyncEnvironment = browserEnvironment()) {
  const cache = client.getQueryCache();
  const pending = new Set<Query>();
  let running = false, disposed = false;
  const ready = () => !disposed && environment.visible() && environment.online() && environment.activity.canRefresh();
  const eligible = (query: Query) => isHouseholdRead(query.queryKey) && query.isActive()
    && query.state.fetchStatus === "idle" && !isDraftDetail(query.queryKey, environment.pathname())
    && !(query.state.error instanceof ApiError && [403, 404, 410].includes(query.state.error.status));

  // Prune cached denied resources even when a route deliberately keeps data during temporary errors.
  const unsubscribeCache = cache.subscribe((event) => {
    if (event.type !== "updated") return;
    const query = event.query;
    if (event.action.type === "error" && query.queryKey[0] === "h") {
      const error = query.state.error;
      if (error instanceof ApiError && [403, 404, 410].includes(error.status) && query.state.data !== undefined) {
        query.setState({ data: undefined, dataUpdatedAt: 0 });
      }
    }
    if (event.action.type === "success" && query.queryKey[0] === "me") {
      const data = query.state.data as { households?: Array<{ id?: unknown }> } | undefined;
      if (!Array.isArray(data?.households) || data.households.some((h) => typeof h.id !== "string")) return;
      const allowed = new Set(data.households.map((h) => h.id));
      const revoked = cache.getAll().filter((q) => q.queryKey[0] === "h" && !allowed.has(q.queryKey[1]));
      // Also invalidate late private-cache writes, including when the user has no household left.
      if (revoked.length) void clearAllPrivateCaches();
      for (const other of revoked) {
        // Cancellation is synchronous inside Query; it prevents late reads from restoring old data.
        void client.cancelQueries({ predicate: (q) => q === other });
        other.setState({ data: undefined, dataUpdatedAt: 0, status: "error", error: new ApiError(404, "not_found", "This household is no longer available.") });
        client.removeQueries({ predicate: (q) => q === other });
      }
    }
  });

  const unsubscribeWrites = environment.activity.subscribe(() => {
    if (!environment.activity.pending()) return;
    for (const query of pending) {
      if (query.state.fetchStatus === "fetching") void client.cancelQueries({ predicate: (q) => q === query });
    }
  });

  const read = async (query: Query) => {
    if (!ready() || !eligible(query) || cache.get(query.queryHash) !== query) return;
    pending.add(query);
    try { await client.refetchQueries({ predicate: (q) => q === query, type: "active" }, { cancelRefetch: false }); }
    finally { pending.delete(query); }
  };
  const refresh = async () => {
    if (running || !ready()) return;
    running = true;
    try {
      const due = cache.getAll().filter((q) => eligible(q) && Date.now() - Math.max(q.state.dataUpdatedAt, q.state.errorUpdatedAt) >= HOUSEHOLD_REFRESH_MS);
      // Learn revoked memberships before starting this round's household reads.
      for (const query of due.filter((q) => q.queryKey[0] === "me")) await read(query);
      const household = due.filter((q) => q.queryKey[0] === "h").sort((a, b) => Math.max(a.state.dataUpdatedAt, a.state.errorUpdatedAt) - Math.max(b.state.dataUpdatedAt, b.state.errorUpdatedAt));
      // The active screen normally has 2–5 queries. Bound pathological observer fan-out per round.
      await Promise.all(household.slice(0, 12).map(read));
    } finally { running = false; }
  };
  const interval = setInterval(() => { void refresh(); }, 1_000);
  const unsubscribeWake = environment.subscribeWake(() => { void refresh(); });
  return () => {
    disposed = true;
    clearInterval(interval);
    unsubscribeCache(); unsubscribeWrites(); unsubscribeWake();
    for (const query of pending) void client.cancelQueries({ predicate: (q) => q === query });
  };
}
